import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

import { useAuth } from '../../contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export function useGestionMembresCooper() {
  const { getAuthHeader } = useAuth();
  const [membres, setMembres] = useState([]);
  const [coopers, setCoopers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // 'all', 'assigned', 'unassigned'
  const [selectedIds, setSelectedIds] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedCooper, setSelectedCooper] = useState('');
  
  // État pour les simulations BATICO
  const [showSimulationsModal, setShowSimulationsModal] = useState(false);
  const [selectedMembreForSimulation, setSelectedMembreForSimulation] = useState(null);
  const [membreSimulations, setMembreSimulations] = useState(null);
  const [loadingSimulations, setLoadingSimulations] = useState(false);

  useEffect(() => {
    fetchData();
  }, [filter]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [statsRes, coopersRes] = await Promise.all([
        axios.get(`${API}/admin/membres-cooper/stats`, { headers: getAuthHeader() }),
        axios.get(`${API}/admin/membres-cooper/coopers/with-capacity`, { headers: getAuthHeader() })
      ]);

      setStats(statsRes.data);
      // Trier du plus récent au plus ancien
      const sortedCoopers = (coopersRes.data.coopers || []).sort((a, b) => new Date(b.date_creation || b.created_at || 0) - new Date(a.date_creation || a.created_at || 0));
      setCoopers(sortedCoopers);

      // Charger les membres selon le filtre
      let membresRes;
      if (filter === 'unassigned') {
        membresRes = await axios.get(`${API}/admin/membres-cooper/membres/unassigned`, {
          headers: getAuthHeader()
        });
      } else if (filter === 'assigned') {
        membresRes = await axios.get(`${API}/admin/membres-cooper/membres/assigned`, {
          headers: getAuthHeader()
        });
      } else {
        // Charger tous les membres
        const [assignedRes, unassignedRes] = await Promise.all([
          axios.get(`${API}/admin/membres-cooper/membres/assigned`, { headers: getAuthHeader() }),
          axios.get(`${API}/admin/membres-cooper/membres/unassigned`, { headers: getAuthHeader() })
        ]);
        membresRes = {
          data: {
            membres: [...assignedRes.data.membres, ...unassignedRes.data.membres]
          }
        };
      }

      // Trier du plus récent au plus ancien
      const sortedMembres = (membresRes.data.membres || []).sort((a, b) => new Date(b.date_creation || b.created_at || 0) - new Date(a.date_creation || a.created_at || 0));
      setMembres(sortedMembres);
      setSelectedIds([]);
    } catch {
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedIds(filteredMembres.map(m => m.id));
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelectOne = (id) => {
    setSelectedIds(prev =>
      prev.includes(id)
        ? prev.filter(selectedId => selectedId !== id)
        : [...prev, id]
    );
  };

  const handleBulkAssign = async () => {
    if (selectedIds.length === 0) {
      toast.error('Aucun membre sélectionné');
      return;
    }

    if (!selectedCooper) {
      toast.error('Veuillez sélectionner un COOPER\'S');
      return;
    }

    try {
      const response = await axios.post(
        `${API}/admin/membres-cooper/bulk-assign`,
        {
          membre_ids: selectedIds,
          cooper_id: selectedCooper
        },
        { headers: getAuthHeader() }
      );

      toast.success(response.data.message);
      if (response.data.errors && response.data.errors.length > 0) {
        toast.warning(`${response.data.error_count} erreur(s)`);
      }

      setShowAssignModal(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'assignation');
    }
  };

  const handleUnassign = async (membreId) => {
    if (!window.confirm('Désassigner ce membre de son COOPER\'S ?')) {
      return;
    }

    try {
      await axios.post(
        `${API}/admin/membres-cooper/unassign/${membreId}`,
        {},
        { headers: getAuthHeader() }
      );

      toast.success('Membre désassigné avec succès');
      fetchData();
    } catch {
      toast.error('Erreur lors de la désassignation');
    }
  };

  // ==================== GESTION SIMULATIONS BATICO ====================
  
  const fetchMembreSimulations = async (membre) => {
    setSelectedMembreForSimulation(membre);
    setLoadingSimulations(true);
    setShowSimulationsModal(true);
    
    try {
      const response = await axios.get(
        `${API}/admin/membres-cooper/membre/${membre.id}/simulations-batico`,
        { headers: getAuthHeader() }
      );
      setMembreSimulations(response.data);
    } catch {
      toast.error('Erreur lors du chargement des simulations');
      setMembreSimulations(null);
    } finally {
      setLoadingSimulations(false);
    }
  };

  const handleResumeSimulation = async (simulation) => {
    try {
      // Récupérer les détails complets de la simulation
      const response = await axios.get(
        `${API}/admin/membres-cooper/membre/${selectedMembreForSimulation.id}/simulation/${simulation.id}/details`,
        { headers: getAuthHeader() }
      );
      
      const simulationData = response.data.simulation;
      
      // Encoder les données pour les passer en URL
      const params = new URLSearchParams();
      params.set('resume', 'true');
      params.set('simulation_id', simulation.id);
      params.set('membre_id', selectedMembreForSimulation.id);
      
      // Données de base
      if (simulationData.territoire) params.set('territoire', simulationData.territoire);
      if (simulationData.type_adhesion) params.set('type_adhesion', simulationData.type_adhesion);
      if (simulationData.ca_ht) params.set('ca_ht', simulationData.ca_ht);
      if (simulationData.nombre_jours) params.set('nombre_jours', simulationData.nombre_jours);
      
      // Activités (encodées en JSON)
      if (simulationData.activites && simulationData.activites.length > 0) {
        params.set('activites', JSON.stringify(simulationData.activites));
      }
      
      // Informations entreprise
      if (simulationData.raison_sociale) params.set('raison_sociale', simulationData.raison_sociale);
      if (simulationData.siret) params.set('siret', simulationData.siret);
      if (simulationData.email) params.set('email', simulationData.email);
      if (simulationData.telephone) params.set('telephone', simulationData.telephone);
      if (simulationData.adresse) params.set('adresse', simulationData.adresse);
      if (simulationData.code_postal) params.set('code_postal', simulationData.code_postal);
      if (simulationData.ville) params.set('ville', simulationData.ville);
      
      // Nominative
      if (simulationData.type_operation) params.set('type_operation', simulationData.type_operation);
      if (simulationData.adresse_operation) params.set('adresse_operation', simulationData.adresse_operation);
      
      // Ouvrir le calculateur BATICO avec les données pré-remplies
      const url = `/calculateur-batico?${params.toString()}`;
      window.open(url, '_blank');
      
      toast.success('Simulation chargée dans le calculateur BATICO');
      setShowSimulationsModal(false);
    } catch {
      toast.error('Erreur lors du chargement de la simulation');
    }
  };

  // Finaliser une simulation directement depuis le modal
  const handleFinalizeSimulation = async (simulation) => {
    if (!window.confirm(`Voulez-vous finaliser cette simulation BATICO pour ${simulation.raison_sociale || 'ce membre'} ?\n\nCela va générer les documents officiels et activer le contrat.`)) {
      return;
    }
    
    try {
      const response = await axios.post(
        `${API}/admin/membres-cooper/membre/${selectedMembreForSimulation.id}/simulation/${simulation.id}/finalize`,
        {},
        { headers: getAuthHeader() }
      );
      
      toast.success('Simulation finalisée avec succès !', {
        description: `Numéro de police: ${response.data.numero_police}`
      });
      
      // Rafraîchir les simulations du membre
      fetchMembreSimulations(selectedMembreForSimulation);
      
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la finalisation');
    }
  };

  const handleCreateNewSimulation = (membre) => {
    // Ouvrir le calculateur BATICO avec les infos du membre pré-remplies
    const params = new URLSearchParams();
    params.set('nouveau', 'true');
    params.set('membre_id', membre.id);
    if (membre.raison_sociale || membre.entreprise) params.set('raison_sociale', membre.raison_sociale || membre.entreprise);
    if (membre.siret) params.set('siret', membre.siret);
    if (membre.email) params.set('email', membre.email);
    if (membre.telephone) params.set('telephone', membre.telephone);
    if (membre.adresse) params.set('adresse', membre.adresse);
    if (membre.code_postal) params.set('code_postal', membre.code_postal);
    if (membre.ville) params.set('ville', membre.ville);
    // Représentant légal + statut juridique + territoire pour un remplissage
    // exhaustif du bloc BÉNÉFICIAIRE / MEMBRE du PDF Proposition ESS,
    // sans dépendre du lookup MongoDB côté backend.
    if (membre.nom) params.set('nom', membre.nom);
    if (membre.prenom) params.set('prenom', membre.prenom);
    if (membre.statut_juridique || membre.forme_juridique) {
      params.set('statut_juridique', membre.statut_juridique || membre.forme_juridique);
    }
    if (membre.territoire) params.set('territoire', membre.territoire);
    
    const url = `/calculateur-batico?${params.toString()}`;
    window.open(url, '_blank');
    toast.info('Nouvelle simulation BATICO ouverte');
  };

  const filteredMembres = membres.filter(m => {
    const matchesSearch = !searchQuery ||
      m.raison_sociale?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      m.siret?.includes(searchQuery) ||
      m.email?.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesSearch;
  });

  return { coopers, fetchData, fetchMembreSimulations, filter, filteredMembres, getAuthHeader, handleBulkAssign, handleCreateNewSimulation, handleFinalizeSimulation, handleResumeSimulation, handleSelectAll, handleSelectOne, handleUnassign, loading, loadingSimulations, membreSimulations, membres, searchQuery, selectedCooper, selectedIds, selectedMembreForSimulation, showAssignModal, showSimulationsModal, stats, setShowAssignModal, setSelectedMembreForSimulation, setSelectedIds, setLoading, setMembres, setSelectedCooper, setFilter, setShowSimulationsModal, setMembreSimulations, setStats, setCoopers, setLoadingSimulations, setSearchQuery };
}
